import { useEffect, useState, useCallback } from "react";
import { RefreshCw, X, Loader2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

const POLL_INTERVAL = 60_000;

export default function VersionCheck() {
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      const devTestVersion = localStorage.getItem("__dev_new_version__");
      if (devTestVersion && devTestVersion !== __GIT_VERSION__) {
        localStorage.removeItem("__dev_new_version__");
        setNewVersion(devTestVersion);
        return;
      }

      const res = await fetch("/version.json", { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      if (data.version && data.version !== __GIT_VERSION__) {
        setNewVersion(data.version);
      }
    } catch {
      // Silencioso
    }
  }, []);

  useEffect(() => {
    checkVersion();
    const interval = setInterval(checkVersion, POLL_INTERVAL);
    const onEvent = () => checkVersion();
    window.addEventListener("version-check", onEvent);
    return () => {
      clearInterval(interval);
      window.removeEventListener("version-check", onEvent);
    };
  }, [checkVersion]);

  if (!newVersion || newVersion === dismissedVersion) return null;

  const handleReload = async () => {
    setReloading(true);

    // Fallback: si tarda mas de 8s, recargar igual
    const fallback = setTimeout(() => window.location.reload(), 8000);

    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }

      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();

        if (reg) {
          await reg.update();

          if (reg.waiting) {
            await new Promise<void>((resolve) => {
              navigator.serviceWorker.addEventListener(
                "controllerchange",
                () => resolve(),
                { once: true },
              );
              reg.waiting!.postMessage({ type: "SKIP_WAITING" });
            });
            window.location.reload();
            return;
          }

          if (reg.installing) {
            await new Promise<void>((resolve) => {
              const timeout = setTimeout(() => resolve(), 5000);
              reg.installing!.addEventListener("statechange", (e) => {
                if ((e.target as ServiceWorker)?.state === "activated") {
                  clearTimeout(timeout);
                  resolve();
                }
              });
            });
          }
        }
      }
    } catch {
      // Si algo falla, recargar igual
    }

    clearTimeout(fallback);
    window.location.reload();
  };

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-9998">
      <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border shadow-lg text-xs">
        <span className="text-muted-foreground">
          Nueva versión{" "}
          <span className="font-mono text-foreground">{newVersion}</span>
        </span>
        <Link
          to="/release-notes"
          className="text-muted-foreground hover:text-foreground underline underline-offset-2"
        >
          novedades
        </Link>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setDismissedVersion(newVersion)}
          className="h-6 w-6 rounded-full p-0"
          title="Omitir"
        >
          <X className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="default"
          onClick={handleReload}
          disabled={reloading}
          className="h-6 rounded-full text-[11px] px-3"
        >
          {reloading ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3 mr-1" />
          )}
          {reloading ? "Actualizando" : "Recargar"}
        </Button>
      </div>
    </div>
  );
}
